import type { FC, Ref, RefObject } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { ApolloError } from '@apollo/client';
import { vegaConversationTranscriptRedesign } from '@gonfalon/dogfood-flags/dogfood-flags';
import { msg } from '@gonfalon/messages/msg';
import {
  type useAgentPromptMutation,
  useGetConversationHistoryLazyQuery,
  useShareConversationMutation,
  useStopAgentMutation,
} from '@gonfalon/observability-graphql/hooks';
import { Box } from '@gonfalon/observability-ui/components/Box';
import { Stack } from '@gonfalon/observability-ui/components/Stack';
import { useProjectId } from '@gonfalon/observability-ui/hooks/useProjectId';
import { useLatestRef } from '@gonfalon/react/useLatestRef';

import { useAgentInsights } from '../../AgentInsightsContext';
import {
  buildConversationErrorComponent,
  currentTurnHasErrorComponent,
  isConversationIncomplete,
  isPlanModeTool,
  NO_INFERENCE_KEY_ERROR_CODE,
  noInferenceKeyMessage,
  processConversationMessage,
  serializeConversation,
  transformConversationHistoryToComponents,
} from '../../agentInsightsUtils';
import type { CompletedComponent, ToolUseMap } from '../../agentInsightsUtils';
import {
  clearConversationFromStorage,
  CONVERSATION_STORAGE_KEY,
  loadClearSignalFromStorage,
  saveClearSignalToStorage,
} from '../../clearConversationFromStorage';
import { firstUserPreviewFromThread } from '../../conversationTitle';
import { InferenceErrorMessage } from '../../internal/components/AgentInsights/components/messages/InferenceErrorMessage';
import { PlanModeMessage } from '../../internal/components/AgentInsights/components/messages/PlanModeMessage';
import { ResultMessage } from '../../internal/components/AgentInsights/components/messages/ResultMessage';
import { UserMessage } from '../../internal/components/AgentInsights/components/messages/UserMessage';
import { useRefetchArtifactsOnSettle } from '../../internal/components/AgentInsights/hooks/useRefetchArtifactsOnSettle';
import type { ResourceOrQuery } from '../../resources';

import { isAlertPreviewData } from './alertPreviewData';
import { ShimmerText } from './components/messages/ShimmerText';
import { ConversationThread } from './ConversationThread';
import { getToolOutputString, isGraphPreviewData, tryParseToolData } from './graphPreviewData';
import { useToolResultRendererContext } from './ToolResultRendererContext';
import type { PromptSubmission, VegaPromptComposerHandle } from './VegaPromptComposer';
import { VegaPromptComposer } from './VegaPromptComposer';

import * as styles from '../../internal/components/AgentInsights/AgentInsights.css';

const AGENT_CREATING_RETRY_COUNT = 10;
const AGENT_CREATING_RETRY_DELAY = 10000; // 10 seconds

/**
 * Given a set of seen turns, advance past the current contiguous turn
 * as far as possible (i.e. while the next turn exists in the set).
 */
function advanceContiguousTurn(seen: Set<number>, current: number): number {
  let next = current;
  while (seen.has(next + 1)) {
    next++;
  }
  return next;
}

const getLatestEditableTextIndex = (completedComponents: CompletedComponent[]) => {
  for (let i = completedComponents.length - 1; i >= 0; i--) {
    if (completedComponents[i].type === 'text') {
      return i;
    }
    if (completedComponents[i].type === 'user') {
      return -1;
    }
  }
  return -1;
};

type AgentPromptReply = {
  conversation_id?: string | null;
};

function isFinalAgentReply(message: object): boolean {
  return 'is_final' in message && Boolean(message.is_final);
}

function agentReplyText(message: object): unknown {
  return 'text' in message ? message.text : undefined;
}

function retryAgentPrompt(options: {
  retryCount: number;
  sendPrompt: () => PromiseLike<AgentPromptReply | null | undefined>;
  setConversationId: (conversationId: string) => void;
  notifyConversationActivity: () => void;
  shouldShareConversation: boolean;
  shareConversation: (conversationId: string) => void;
  addComponent: (component: CompletedComponent) => void;
  setIsAnalyzing: (isAnalyzing: boolean) => void;
  startPolling: (conversationId: string) => void;
  setLoadingText: (loadingText: string) => void;
}): Promise<void> {
  const attempt = (retryCount: number): Promise<void> =>
    Promise.resolve(options.sendPrompt())
      .then((agentMessage) => {
        if (!agentMessage?.conversation_id) {
          return;
        }
        const conversationId = agentMessage.conversation_id;
        options.setConversationId(conversationId);

        // The prompt is now persisted, so the conversation's
        // last-user-prompt time has advanced. Signal activity so the
        // "Most recent chats" sidebar refetches and re-sorts this chat to
        // the top without a page reload.
        options.notifyConversationActivity();

        if (options.shouldShareConversation) {
          options.shareConversation(conversationId);
        }

        if (isFinalAgentReply(agentMessage)) {
          options.addComponent({
            type: 'text',
            content: agentReplyText(agentMessage),
          });
          options.setIsAnalyzing(false);
          return;
        }

        options.startPolling(conversationId);
      })
      .catch((err: Error) => {
        const noKeyMessage = noInferenceKeyMessage(err);
        if (noKeyMessage) {
          options.setIsAnalyzing(false);
          options.addComponent({
            type: 'error',
            content: noKeyMessage,
            errorCode: NO_INFERENCE_KEY_ERROR_CODE,
            errorMessage: noKeyMessage,
          });
          return;
        }
        // Only retry if the error is because the agent is still creating
        if (!err.message.includes('status: Creating') || retryCount <= 0) {
          options.setIsAnalyzing(false);
          const forbidden =
            err instanceof ApolloError && err.graphQLErrors.some((error) => error.extensions?.code === 'forbidden');
          options.addComponent({
            type: 'error',
            content: msg(forbidden ? 'vega.conversation.errorForbiddenBody' : 'vega.conversation.errorDefaultBody'),
            errorCode: forbidden ? 'forbidden' : undefined,
          });
          return;
        }

        options.setLoadingText(msg('vega.conversation.startingAgentTask'));

        setTimeout(() => {
          void attempt(retryCount - 1);
        }, AGENT_CREATING_RETRY_DELAY);
      });

  return attempt(options.retryCount);
}

// Type for persisted conversation state
export interface PersistedConversationState {
  conversationId: string | null;
  completedComponents: Array<{
    type: 'text' | 'tool' | 'user' | 'error';
    content: unknown;
    toolName?: string;
    rawToolName?: string;
    context?: unknown;
    toolInputs?: unknown;
    errorCode?: string;
    errorMessage?: string;
  }>;
  projectId: string;
}

// Utility functions for session storage
const saveConversationToStorage = (state: PersistedConversationState) => {
  try {
    sessionStorage.setItem(CONVERSATION_STORAGE_KEY, JSON.stringify(state));
  } catch (error) {
    console.warn('Failed to save conversation to session storage:', error);
  }
};

const loadConversationFromStorage = (currentProjectId: string): PersistedConversationState | null => {
  try {
    const stored = sessionStorage.getItem(CONVERSATION_STORAGE_KEY);
    if (!stored) {
      return null;
    }

    const parsed = JSON.parse(stored) as PersistedConversationState;

    // Only restore if it's for the same project
    if (parsed.projectId !== currentProjectId) {
      return null;
    }

    return parsed;
  } catch (error) {
    console.warn('Failed to load conversation from session storage:', error);
    return null;
  }
};

/**
 * Decide whether sessionStorage state should be restored into the component.
 * Returns false if the stored state belongs to a different conversation than the one
 * the route is asking to load — in that case the caller should purge storage and let
 * the fresh fetch populate state instead.
 */
export const shouldRestoreStoredState = (
  storedState: PersistedConversationState | null,
  conversationIdToLoad: string | null,
  currentConversationId: string | null = null,
): boolean => {
  if (!storedState) {
    return false;
  }
  if (conversationIdToLoad && storedState.conversationId !== conversationIdToLoad) {
    return false;
  }
  // History clicks set currentConversationId before remount and leave
  // conversationIdToLoad unset. Do not restore a different stored thread.
  if (currentConversationId && storedState.conversationId !== currentConversationId) {
    return false;
  }
  return true;
};

export type AgentPromptMutationFn = ReturnType<typeof useAgentPromptMutation>[0];

export type SendPromptFn = (prompt: string) => Promise<{ conversation_id: string }>;

export type VegaSendPathProps =
  | { agentPromptMutation: AgentPromptMutationFn; onSendPrompt?: never }
  | { agentPromptMutation?: never; onSendPrompt: SendPromptFn };

export type AgentInsightsProps = VegaSendPathProps & {
  onClearRef?: RefObject<(() => void) | null>;
  onFocusRef?: RefObject<(() => void) | null>;
  onClearPromptRef?: RefObject<(() => void) | null>;
  /** Forwarded to the composer. See VegaPromptComposerHandle. */
  composerRef?: Ref<VegaPromptComposerHandle>;
  isLoadingConversation?: boolean;
  aiChatLimitData?: {
    aiChatLimitStatus: {
      current_count: number;
      limit: number;
    } | null;
  };
  simplifiedFooter?: boolean;
  sending: boolean;
  enableAgentResponseSelector?: boolean;
  onUserPrompt?: () => void;
  canStopGeneration?: boolean;
  persistToSessionStorage?: boolean;
};

export const AgentInsights: FC<AgentInsightsProps> = ({
  onClearRef,
  onFocusRef,
  onClearPromptRef,
  composerRef,
  isLoadingConversation,
  aiChatLimitData,
  simplifiedFooter = false,
  agentPromptMutation,
  onSendPrompt,
  sending,
  enableAgentResponseSelector = false,
  onUserPrompt,
  canStopGeneration = true,
  persistToSessionStorage = true,
}) => {
  const {
    context: agentContext,
    clear,
    clearContext,
    isOpen,
    persistConversation,
    clearRetrievedConversationData,
    retrievedConversationData,
    currentConversationId,
    conversationIdToLoad,
    setCurrentConversationId,
    notifyGraphMutation,
    notifyConversationActivity,
    shareConversation: shouldShareConversation,
    conversationClearSignal,
    permissionMode,
    registerConversationText,
    setFirstUserMessagePreview,
  } = useAgentInsights();
  const toolResultRenderer = useToolResultRendererContext();

  // Conversation id returned by the first call to agentPrompt. Subsequent
  // prompts will continue the same conversation.
  const [conversationId, setConversationId] = useState<string | null>(null);
  // Loading state for analysis
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  // Refresh the PR badge / Artifacts tab with whatever the run produced.
  const refetchArtifacts = useRefetchArtifactsOnSettle(isAnalyzing);
  // Track if user is at bottom of scroll
  const [isAtBottom, setIsAtBottom] = useState(true);
  // Track list of completed components (text, tool, or user)
  const [completedComponents, setCompletedComponents] = useState<CompletedComponent[]>([]);

  // Expose the conversation to the layout header's "Copy conversation" control.
  // Keep the latest components in a ref so the registered serializer has a stable
  // identity and always reads current data without re-registering each turn.
  const completedComponentsRef = useLatestRef(completedComponents);
  useEffect(() => {
    registerConversationText(() => serializeConversation(completedComponentsRef.current));
    return () => registerConversationText(null);
  }, [registerConversationText, completedComponentsRef]);
  // Do not reset this on clear: an in-flight poll can still deliver leftover
  // user messages, and a false→true edge would write the old prompt back.
  const hadUserMessageRef = useRef(false);
  useEffect(() => {
    const { preview, hadUser } = firstUserPreviewFromThread({
      components: completedComponents,
      previousHadUser: hadUserMessageRef.current,
    });
    hadUserMessageRef.current = hadUser;
    if (preview !== undefined) {
      setFirstUserMessagePreview(preview);
    }
  }, [completedComponents, setFirstUserMessagePreview, hadUserMessageRef]);
  // Track the agent response that has been selected by the user (eg: checkbox selections)
  const [agentResponse, setAgentResponse] = useState('');
  // Track current loading state text
  const [loadingText, setLoadingText] = useState<string>(msg('vega.conversation.thinking'));
  // Turn-based polling state: only advance when turns are contiguous so
  // out-of-order arrivals don't cause us to skip messages.
  const lastContiguousTurn = useRef<number>(0);
  const seenTurns = useRef<Set<number>>(new Set());
  // Persistent tool_use map shared across history loading and polling so
  // tool_result messages can always resolve their corresponding tool_use.
  const toolUseMapRef = useRef<ToolUseMap>(new Map());
  // Interval handle for the polling loop
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Scroll container ref so we can auto-scroll as new messages arrive, and a
  // sentinel at the end of the thread used for measurements/rendering.
  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);

  // Scroll only the messages container to the bottom. We deliberately set the
  // container's own scrollTop instead of calling `scrollIntoView`, because
  // `scrollIntoView` scrolls every scrollable ancestor (including the page),
  // which previously yanked the layout header out of view until a refresh.
  const scrollToBottom = useCallback(
    (behavior: ScrollBehavior = 'smooth') => {
      const el = scrollContainerRef.current;
      if (el) {
        el.scrollTo({ top: el.scrollHeight, behavior });
      }
    },
    [scrollContainerRef],
  );

  // Tracks whether the user manually stopped generation in the current session.
  const [stoppedManually, setStoppedManually] = useState(false);
  const showIncompleteWarning = isConversationIncomplete(retrievedConversationData) || stoppedManually;

  // Track previous warning state so we can react to the false -> true transition only.
  const prevShowIncompleteWarning = useRef(false);

  const { projectId } = useProjectId();
  const [getConversationHistory] = useGetConversationHistoryLazyQuery();
  const [stopAgentMutation] = useStopAgentMutation();
  const [shareConversationMutation] = useShareConversationMutation();

  // Restore conversation state from session storage on mount, but only when the stored state
  // matches the conversation the route is asking to load. A mismatch means storage belongs to
  // a different conversation (e.g. home -> submit -> /vega/:newId while storage still holds
  // :oldId's messages) -- in that case purge storage and skip the restore so the route's
  // fetch populates `retrievedConversationData` without competing with stale state.
  //
  // Intentionally does NOT wipe local state (`completedComponents`, `conversationId`) on
  // mismatch: doing so can cause a blank flash when AgentInsights stays mounted across a
  // conversation switch, since the `retrievedConversationData` effect that repaints messages
  // only re-fires when its dep reference changes. Leaving local state untouched keeps the
  // previous conversation's messages visible until the new fetch lands, and the loading
  // indicator (driven by `isLoadingConversation` from the route) covers the fresh-mount case.
  const conversationIdRef = useLatestRef(conversationId);
  const historyAppliedRef = useRef(false);
  const restoreGenerationRef = useRef(0);
  useEffect(() => {
    const generation = restoreGenerationRef.current + 1;
    restoreGenerationRef.current = generation;
    if (!projectId) {
      return;
    }
    if (
      retrievedConversationData &&
      currentConversationId &&
      retrievedConversationData.conversationId === currentConversationId
    ) {
      historyAppliedRef.current = true;
      return;
    }
    if (currentConversationId && currentConversationId === conversationIdRef.current) {
      return;
    }
    historyAppliedRef.current = false;
    if (!persistToSessionStorage) {
      return;
    }
    const storedState = loadConversationFromStorage(projectId);

    if (!shouldRestoreStoredState(storedState, conversationIdToLoad, currentConversationId)) {
      if (storedState) {
        clearConversationFromStorage();
      }
      return;
    }

    if (storedState) {
      queueMicrotask(() => {
        if (generation !== restoreGenerationRef.current || historyAppliedRef.current) {
          return;
        }
        const latest = loadConversationFromStorage(projectId);
        if (!latest || !shouldRestoreStoredState(latest, conversationIdToLoad, currentConversationId)) {
          return;
        }
        setConversationId(latest.conversationId);
        setCompletedComponents(latest.completedComponents);
      });
    }

    setTimeout(() => {
      scrollToBottom('auto');
    }, 0);
  }, [
    conversationIdRef,
    projectId,
    conversationIdToLoad,
    currentConversationId,
    retrievedConversationData,
    scrollToBottom,
    persistToSessionStorage,
  ]);

  // Save conversation state to session storage whenever it changes
  useEffect(() => {
    if (persistToSessionStorage && projectId && (conversationId || completedComponents.length > 0)) {
      const state: PersistedConversationState = {
        conversationId,
        completedComponents,
        projectId,
      };
      saveConversationToStorage(state);
    }
  }, [conversationId, completedComponents, projectId, persistToSessionStorage]);

  const stopPolling = useCallback(() => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
  }, [pollingRef]);

  // Clear only local conversation state (preserves context from provider)
  const clearConversationState = useCallback(() => {
    setConversationId(null);
    stopPolling();
    lastContiguousTurn.current = 0;
    seenTurns.current = new Set();
    toolUseMapRef.current = new Map();
    setStoppedManually(false);
    setIsAnalyzing(false);
    setCompletedComponents([]);
    setLoadingText(msg('vega.conversation.thinking'));
    setAgentResponse('');
    if (persistToSessionStorage) {
      clearConversationFromStorage();
    }
  }, [stopPolling, lastContiguousTurn, seenTurns, toolUseMapRef, persistToSessionStorage]);

  // Clear conversation but preserve context (for "New chat" button)
  // Context can be cleared manually using the 'x' button on context items
  const handleClear = useCallback(() => {
    // Clear conversation state but preserve context items
    clear({ preserveContext: true });
    // Clear local component state
    clearConversationState();
  }, [clear, clearConversationState]);

  const clearConversationStateRef = useRef(clearConversationState);
  const scrollToBottomRef = useRef(scrollToBottom);
  const completedComponentsCountRef = useRef(completedComponents.length);
  useEffect(() => {
    clearConversationStateRef.current = clearConversationState;
    scrollToBottomRef.current = scrollToBottom;
    completedComponentsCountRef.current = completedComponents.length;
  }, [clearConversationState, scrollToBottom, completedComponents.length]);

  // Scroll to bottom when panel opens and there are existing messages
  // clear the conversation if persistConversation is false (but preserve context)
  useEffect(() => {
    if (!persistConversation) {
      clearConversationStateRef.current();
    } else if (isOpen && completedComponentsCountRef.current > 0) {
      setTimeout(() => {
        scrollToBottomRef.current('auto');
      }, 0);
    }
  }, [isOpen, persistConversation, clearConversationStateRef, scrollToBottomRef, completedComponentsCountRef]);

  // Reset local conversation state when the provider explicitly bumps the
  // clear signal (e.g. clicking an external source link starts a new
  // conversation while the panel is already open and persistConversation
  // hasn't transitioned).
  //
  // Initialize from sessionStorage so a signal bumped while this component
  // was unmounted (History view replaces AgentInsights) is detected on
  // remount. On page reload both conversationClearSignal (useState(0)) and
  // the stored value reset in sync, so post-reload restores are unaffected.
  const lastSeenClearSignal = useRef(
    persistToSessionStorage ? (loadClearSignalFromStorage() ?? conversationClearSignal) : conversationClearSignal,
  );
  useEffect(() => {
    if (conversationClearSignal !== lastSeenClearSignal.current) {
      lastSeenClearSignal.current = conversationClearSignal;
      if (persistToSessionStorage) {
        saveClearSignalToStorage(conversationClearSignal);
      }
      clearConversationStateRef.current();
    }
  }, [conversationClearSignal, lastSeenClearSignal, clearConversationStateRef, persistToSessionStorage]);

  // Sync conversation ID with context
  useEffect(() => {
    if (conversationId) {
      setCurrentConversationId(conversationId);
    }
  }, [conversationId, setCurrentConversationId]);

  const addToConversationAndLog = useCallback((component: CompletedComponent) => {
    setCompletedComponents((prev) => [...prev, component]);
  }, []);

  // Process new messages from polling incrementally. Messages are sorted by
  // turn and deduplicated so out-of-order arrivals are handled gracefully.
  const processPolledMessages = useCallback(
    (
      newMessages: Array<{
        id: string;
        role: string;
        content: any;
        turn: number;
        is_final: boolean;
      }>,
    ) => {
      const newComponents: CompletedComponent[] = [];

      const sorted = [...newMessages].sort((a, b) => a.turn - b.turn);

      for (const polledMessage of sorted) {
        if (seenTurns.current.has(polledMessage.turn)) {
          continue;
        }
        seenTurns.current.add(polledMessage.turn);

        if (polledMessage.role === 'user') {
          continue;
        }

        try {
          const { components } = processConversationMessage(polledMessage, toolUseMapRef.current);
          newComponents.push(...components);
        } catch (error) {
          console.error('Error processing polled message:', error);
        }
      }

      lastContiguousTurn.current = advanceContiguousTurn(seenTurns.current, lastContiguousTurn.current);

      if (newComponents.length > 0) {
        setCompletedComponents((prev) => [...prev, ...newComponents]);

        // A typed `vega_error` envelope (e.g. INFERENCE_UNAVAILABLE)
        // means the agent terminated early. Stop polling and clear the
        // analyzing spinner so the user sees the inline banner instead
        // of an indefinite "Thinking..." indicator. The `agentPrompt`
        // mutation will already have stored the same message with
        // is_final=true on the backend, but stopping locally avoids one
        // extra poll round-trip and removes the spinner immediately.
        if (newComponents.some((c) => c.type === 'error')) {
          stopPolling();
          setIsAnalyzing(false);
        }

        // Fire the dashboard-mutation signal so an open `DashboardPage`
        // refetches its `useGetVisualizationQuery` and picks up the change
        // without a manual page reload. Brief tools are listed alongside
        // graph tools because the renderer (`BriefDashboard`) consumes the
        // same query — it re-renders when `briefContent` updates.
        if (
          newComponents.some(
            (c) =>
              c.rawToolName === 'mcp__observability__create_graph' ||
              c.rawToolName === 'mcp__observability__create_dashboard' ||
              c.rawToolName === 'mcp__observability__create_brief_dashboard' ||
              c.rawToolName === 'mcp__observability__update_brief_dashboard',
          )
        ) {
          notifyGraphMutation();
        }
      }
    },
    [notifyGraphMutation, stopPolling, seenTurns, toolUseMapRef, lastContiguousTurn],
  );

  // Start polling for new messages on a given conversation
  const startPolling = useCallback(
    (convId: string) => {
      stopPolling();

      const poll = () => {
        getConversationHistory({
          variables: {
            conversation_id: convId,
            after_turn: lastContiguousTurn.current,
          },
          fetchPolicy: 'no-cache',
        })
          .then(({ data }) => {
            const response = data?.conversation_history;
            const msgs = response?.messages;
            if (msgs && msgs.length > 0) {
              processPolledMessages(msgs);
            }

            if (response?.status === 'completed' || response?.status === 'stopped') {
              stopPolling();
              setIsAnalyzing(false);
            }

            // A failed conversation is terminal: surface the failure reason
            // as an inline error banner instead of polling the spinner
            // forever. One banner per failed turn — skip if the current turn
            // already produced one (e.g. a typed vega_error envelope, or a
            // second in-flight poll resolving after this one).
            if (response?.status === 'failed') {
              stopPolling();
              setIsAnalyzing(false);
              setCompletedComponents((prev) =>
                currentTurnHasErrorComponent(prev) ? prev : [...prev, buildConversationErrorComponent(response.error)],
              );
            }
          })
          .catch((err: unknown) => {
            console.error('Polling error (messages):', err);
          });
      };

      // Fire the first poll immediately, then every 1s
      poll();
      pollingRef.current = setInterval(poll, 1000);
    },
    [stopPolling, getConversationHistory, processPolledMessages, lastContiguousTurn, pollingRef],
  );

  // Handle retrieved conversation data from the context
  useEffect(() => {
    // History remounts this tree. Ignore a leftover snapshot whose id no
    // longer matches chrome so leftover user messages cannot become the title.
    if (!retrievedConversationData || retrievedConversationData.conversationId !== currentConversationId) {
      return;
    }

    historyAppliedRef.current = true;
    toolUseMapRef.current = new Map();
    const {
      conversationId: retrievedId,
      completedComponents: retrievedComponents,
      status,
      isFinal,
    } = transformConversationHistoryToComponents(retrievedConversationData, toolUseMapRef.current);

    setConversationId(retrievedId);
    setCurrentConversationId(retrievedId);
    setCompletedComponents(retrievedComponents);

    setStoppedManually(status === 'stopped');

    // Populate turn-based tracking from the loaded history so polling
    // only fetches newer messages.
    const allTurns = retrievedConversationData.messages.map((m) => m.turn);
    seenTurns.current = new Set(allTurns);
    lastContiguousTurn.current = advanceContiguousTurn(seenTurns.current, 0);

    // Stop existing polling before deciding if we need to start new polling
    stopPolling();

    if (!isFinal) {
      setIsAnalyzing(true);
      startPolling(retrievedId);
    } else {
      setIsAnalyzing(false);
    }
  }, [
    retrievedConversationData,
    currentConversationId,
    setCurrentConversationId,
    startPolling,
    stopPolling,
    toolUseMapRef,
    seenTurns,
    lastContiguousTurn,
  ]);

  // Clean up polling on unmount
  useEffect(() => {
    return () => {
      stopPolling();
    };
  }, [stopPolling]);

  const handleStopGeneration = useCallback(() => {
    setStoppedManually(true);
    stopPolling();
    setIsAnalyzing(false);
    setLoadingText(msg('vega.conversation.thinking'));

    if (conversationId) {
      stopAgentMutation({
        variables: { conversation_id: conversationId },
      })
        .then(() => {
          // `isAnalyzing` was cleared above, so the settle effect already refetched — before the
          // agent finished shutting down. Anything it wrote on the way out lands here.
          refetchArtifacts();
        })
        .catch((err: unknown) => {
          console.error('Failed to stop agent:', err);
        });
    }
  }, [conversationId, stopPolling, stopAgentMutation, refetchArtifacts]);

  // Check if user is at bottom of scroll
  const checkIfAtBottom = useCallback(() => {
    if (scrollContainerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = scrollContainerRef.current;
      const atBottom = scrollTop + clientHeight >= scrollHeight - 5; // 5px threshold
      setIsAtBottom(atBottom);
    }
  }, [scrollContainerRef]);

  // Handle scroll events
  const handleScroll = useCallback(() => {
    checkIfAtBottom();
  }, [checkIfAtBottom]);

  // Auto-scroll only when user is at bottom
  useEffect(() => {
    if (isAtBottom) {
      scrollToBottom(completedComponents.length === 0 ? 'smooth' : 'smooth');
    }
  }, [completedComponents, isAtBottom, scrollToBottom]);

  // When the incomplete-conversation warning transitions from hidden to shown,
  // smooth-scroll the bottom of the thread into view so the user actually
  // notices that Vega has stopped. Only fires on the transition — if the user
  // scrolls away after that, we don't keep yanking them back.
  useEffect(() => {
    if (showIncompleteWarning && !prevShowIncompleteWarning.current) {
      scrollToBottom('smooth');
    }
    prevShowIncompleteWarning.current = showIncompleteWarning;
  }, [showIncompleteWarning, scrollToBottom, prevShowIncompleteWarning]);

  // When the conversation has stopped and the user is on a different tab,
  // prefix the document title so they notice when they tab back. We restore
  // the original title when the tab regains focus or the warning clears.
  useEffect(() => {
    if (!showIncompleteWarning) {
      return;
    }

    const TITLE_PREFIX = '(stopped) ';
    const applyBadge = () => {
      if (document.visibilityState === 'hidden' && !document.title.startsWith(TITLE_PREFIX)) {
        const currentTitle = document.title.startsWith(TITLE_PREFIX)
          ? document.title.slice(TITLE_PREFIX.length)
          : document.title;
        document.title = `${TITLE_PREFIX}${currentTitle}`;
      }
    };

    const removeBadge = () => {
      if (document.title.startsWith(TITLE_PREFIX)) {
        document.title = document.title.slice(TITLE_PREFIX.length);
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        removeBadge();
      } else {
        applyBadge();
      }
    };

    applyBadge();
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      removeBadge();
    };
  }, [showIncompleteWarning]);

  // Set up scroll listener
  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', handleScroll);
      // Check initial position
      checkIfAtBottom();

      return () => {
        scrollContainer.removeEventListener('scroll', handleScroll);
      };
    }
  }, [handleScroll, checkIfAtBottom, scrollContainerRef]);

  const handlePromptSubmit = useCallback(
    ({ rawPrompt, prompt: enhancedPrompt, repositories }: PromptSubmission) => {
      onUserPrompt?.();
      // Clear loaded conversation snapshot so the banner disappears when resuming
      clearRetrievedConversationData();

      // Add user message to completed components immediately (show what the user typed, not the enhanced prompt)
      addToConversationAndLog({
        type: 'user',
        content: rawPrompt,
        context: agentContext?.resourceOrQuery,
        timestamp: new Date().toISOString(),
      });

      // Clear the context after sending (so it doesn't appear for subsequent messages)
      clearContext();

      setIsAnalyzing(true);
      setLoadingText(msg('vega.conversation.thinking'));
      // Clear any checkbox selections at submit time so they don't linger if the
      // mutation fails — otherwise the send button would stay enabled and a retry
      // click would re-submit the stale selections as a duplicate user message.
      setAgentResponse('');

      setStoppedManually(false);

      const sendPrompt = () => {
        if (onSendPrompt) {
          return onSendPrompt(enhancedPrompt);
        }
        return agentPromptMutation({
          variables: {
            input: {
              prompt: enhancedPrompt,
              conversation_id: conversationId ?? undefined,
              project_id: projectId,
              repositories,
              permission_mode: permissionMode,
            },
          },
        }).then(({ data }) => data?.agentPrompt);
      };

      void retryAgentPrompt({
        retryCount: AGENT_CREATING_RETRY_COUNT,
        sendPrompt,
        setConversationId,
        notifyConversationActivity,
        shouldShareConversation,
        shareConversation: (sharedConversationId) => {
          shareConversationMutation({
            variables: {
              project_id: projectId,
              conversation_id: sharedConversationId,
            },
          }).catch(() => {});
        },
        addComponent: addToConversationAndLog,
        setIsAnalyzing,
        startPolling,
        setLoadingText,
      });
    },
    [
      conversationId,
      agentPromptMutation,
      onSendPrompt,
      projectId,
      agentContext,
      clearContext,
      clearRetrievedConversationData,
      startPolling,
      shouldShareConversation,
      shareConversationMutation,
      permissionMode,
      notifyConversationActivity,
      onUserPrompt,
      addToConversationAndLog,
    ],
  );

  useEffect(() => {
    if (onClearRef) {
      onClearRef.current = handleClear;
    }
  }, [onClearRef, handleClear]);

  // Only the latest text result is editable — a user follow-up locks the prior result.
  const latestEditableTextIndex = getLatestEditableTextIndex(completedComponents);
  const redesign = vegaConversationTranscriptRedesign();

  return (
    <>
      {/* oxlint-disable-next-line gonfalon/no-inline-copy -- keyframes CSS, not copy */}
      <style>{`
        @keyframes shine {
          0% {
            background-position: 300% 0;
          }
          100% {
            background-position: -300% 0;
          }
        }
      `}</style>
      <Box display="flex" flexDirection="column" style={{ height: '100%' }}>
        {/* Scrollable content area. Full-bleed on purpose so the scrollbar tracks the pane's
            right edge; `messageColumn` re-applies the capped, centered measure inside. */}
        <Box py="24" flexGrow={1} style={{ overflow: 'auto' }} ref={scrollContainerRef}>
          <div className={styles.messageColumn}>
            {/* Show loading state when loading conversation */}
            {isLoadingConversation && (
              <Box marginBottom="24">
                <Stack gap="0">
                  <ShimmerText text={msg('vega.conversation.loadingConversation')} isLoading />
                  <div ref={messagesEndRef} />
                </Stack>
              </Box>
            )}

            {!isLoadingConversation && completedComponents.length > 0 && (
              <Box marginBottom="24">
                <Stack gap="0">
                  {redesign ? (
                    <ConversationThread
                      components={completedComponents}
                      isAnalyzing={isAnalyzing}
                      loadingText={loadingText}
                      latestEditableTextIndex={latestEditableTextIndex}
                      onSetAgentResponse={enableAgentResponseSelector ? setAgentResponse : undefined}
                    />
                  ) : null}
                  {!redesign &&
                    completedComponents.map((component, index) => {
                      if (component.type === 'user') {
                        return (
                          <UserMessage
                            key={index}
                            message={component.content as string}
                            context={component.context as ResourceOrQuery[] | undefined}
                            timestamp={component.timestamp}
                          />
                        );
                      }
                      if (component.type === 'text') {
                        const isLatest = index === latestEditableTextIndex;
                        return (
                          <ResultMessage
                            key={index}
                            message={{ text: component.content }}
                            timestamp={component.timestamp}
                            onSetAgentResponse={enableAgentResponseSelector && isLatest ? setAgentResponse : undefined}
                          />
                        );
                      }
                      if (component.type === 'error') {
                        return (
                          <InferenceErrorMessage
                            key={index}
                            errorCode={component.errorCode}
                            errorMessage={component.errorMessage}
                          />
                        );
                      }
                      if (component.type === 'tool' && component.rawToolName && isPlanModeTool(component.rawToolName)) {
                        return (
                          <PlanModeMessage
                            key={index}
                            variant={component.rawToolName === 'EnterPlanMode' ? 'enter' : 'exit'}
                            toolInputs={component.rawToolName === 'ExitPlanMode' ? component.toolInputs : undefined}
                          />
                        );
                      }
                      if (component.type === 'tool') {
                        // Auto-expand graph/alert previews so users see charts immediately
                        const shouldAutoExpand = component.content
                          ? (() => {
                              const parsed = tryParseToolData(component.content);
                              return isGraphPreviewData(parsed) || isAlertPreviewData(parsed);
                            })()
                          : false;

                        // Bash/command tools with no output shouldn't show loading (e.g., from conversation history).
                        // Unwrap content envelopes so an empty `{ content: [{ text: '' }] }` is treated as no output.
                        const hasCommandInput =
                          component.toolInputs &&
                          typeof (component.toolInputs as { command?: string }).command === 'string';
                        const outputStr = getToolOutputString(component.content);
                        const isCommandWithNoOutput = !!hasCommandInput && !outputStr.trim();
                        const isLoading = !isCommandWithNoOutput && !component.content;

                        const renderContent =
                          component.content && !isCommandWithNoOutput
                            ? () => toolResultRenderer(component.content, component.toolInputs)
                            : undefined;

                        return (
                          <ShimmerText
                            key={index}
                            text={component.toolName ?? ''}
                            isLoading={isLoading}
                            toolInputs={component.toolInputs}
                            onClick={() => {}}
                            startExpanded={shouldAutoExpand}
                            renderContent={renderContent}
                          />
                        );
                      }
                      return null;
                    })}

                  {/* Show dynamic loading shimmer when analyzing */}
                  {!redesign && isAnalyzing && <ShimmerText text={loadingText} isLoading />}
                  <div ref={messagesEndRef} />
                </Stack>
              </Box>
            )}
          </div>
        </Box>

        <div className={styles.contentColumn}>
          <VegaPromptComposer
            agentResponse={agentResponse}
            onSubmit={handlePromptSubmit}
            aiChatLimitData={aiChatLimitData}
            disabled={sending}
            isAnalyzing={isAnalyzing}
            onStopGeneration={canStopGeneration ? handleStopGeneration : undefined}
            showIncompleteWarning={showIncompleteWarning}
            showSuggestedPrompts={completedComponents.length === 0}
            conversationId={conversationId ?? undefined}
            onFocusRef={onFocusRef}
            onClearPromptRef={onClearPromptRef}
            ref={composerRef}
            simplifiedFooter={simplifiedFooter}
          />
        </div>
      </Box>
    </>
  );
};
